import React from "react";
import PropTypes from "prop-types";

// CompetitionToolbar is the Model Studio header: a title + meta line, a short
// "what this drives" description, and the two primary actions grouped together
// (Run competition + Promote/Apply). Folding the promote action up here — rather
// than a separate bar below the table — keeps every control in one place.
export default function CompetitionToolbar({
  meta,
  running,
  onRun,
  scope,
  subtitle,
  help,
  overrides,
  promoting,
  onPromote,
}) {
  const seriesCount = new Set((meta?.results || []).map((r) => r.series_name)).size;
  const isRunning = running || meta?.running;

  let metaLine;
  if (meta?.running) {
    metaLine = "Competition running…";
  } else if (seriesCount > 0) {
    metaLine = `Last run · ${seriesCount} series`;
  } else if (meta?.runId) {
    metaLine = "Last run · no results";
  } else {
    metaLine = "No competition run yet";
  }

  const isAllocation = scope === "allocation";
  const promoteIdle = isAllocation ? "Apply now" : "Promote to dashboard";
  const promoteBusy = isAllocation ? "Applying…" : "Promoting…";
  const noun = isAllocation ? "source" : "series";

  return (
    <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="max-w-xl">
        <div className="flex items-center gap-1.5">
          <h2 className="text-base font-semibold text-text">Per-series model competition</h2>
          {help && (
            <span
              className="cursor-help text-sm leading-none text-text-3"
              data-tooltip-id="instantTooltip"
              data-tooltip-content={help}
              aria-label={help}
            >
              ⓘ
            </span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-text-3">
          {metaLine}
          {subtitle ? ` · ${subtitle}` : ""}
        </p>
      </div>

      <div className="flex shrink-0 flex-col items-stretch gap-1.5 sm:items-end">
        <div className="flex items-center gap-2">
          {/* Run is the iterate action (secondary); Promote commits the winners to
              the live forecasts (primary). */}
          <button
            type="button"
            onClick={onRun}
            disabled={isRunning}
            className="inline-flex items-center rounded-ctrl border border-card-bd bg-card px-3 py-1.5 text-sm font-medium text-text-2 transition-colors hover:border-border-strong hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isRunning ? "Running…" : "Run competition"}
          </button>
          <button
            type="button"
            onClick={onPromote}
            disabled={promoting}
            className="inline-flex items-center rounded-ctrl bg-success px-3 py-1.5 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {promoting ? promoteBusy : promoteIdle}
          </button>
        </div>
        <p className="text-xs text-text-3">
          {overrides} override{overrides === 1 ? "" : "s"} · Prophet is the fallback for unmapped {noun}
        </p>
      </div>
    </div>
  );
}

CompetitionToolbar.propTypes = {
  meta: PropTypes.shape({
    runId: PropTypes.number,
    running: PropTypes.bool,
    results: PropTypes.array,
  }),
  running: PropTypes.bool,
  onRun: PropTypes.func.isRequired,
  scope: PropTypes.string,
  subtitle: PropTypes.string,
  help: PropTypes.string,
  overrides: PropTypes.number,
  promoting: PropTypes.bool,
  onPromote: PropTypes.func.isRequired,
};

CompetitionToolbar.defaultProps = {
  meta: null,
  running: false,
  scope: "insights",
  subtitle: null,
  help: null,
  overrides: 0,
  promoting: false,
};
